import { IQuoteLineItem } from "./types";
import { EditorSection, EditorSectionDates, newEditorSection } from "../components/QuoteSectionBar/sectionUtils";

// The bundle of fields both CreateQuotePage and QuoteDetailPage keep as individual useState -
// what a restore actually rehydrates. Deliberately excludes anything server-computed (pricing
// output, proration detail, etc.) - that's always re-derived fresh after a restore, never trusted
// from a draft.
export type DraftableEditorState = {
  prospectName: string;
  name: string;
  description: string;
  // QuoteDetailPage only (CreateQuotePage has no internal note field yet) - carried here anyway so
  // one applyQuoteDraft/DraftableEditorState pair serves both editor pages identically.
  internalNote: string;
  hasAffiliate: boolean;
  affiliateName: string;
  customerGuid: string | null;
  discounts: { monthly: string; installment: string; annual: string };
  sections: EditorSection[];
};

const isStr = (value: unknown): value is string => typeof value === "string";
const isBool = (value: unknown): value is boolean => typeof value === "boolean";
const isNum = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
const isNullableNum = (value: unknown): value is number | null => value === null || isNum(value);
const isNullableStr = (value: unknown): value is string | null => value === null || isStr(value);
const isPercent = (value: unknown): value is string => isStr(value) && value !== "" && Number.isFinite(Number(value));
const record = (value: unknown): Record<string, unknown> | null => typeof value === "object" && value !== null ? value as Record<string, unknown> : null;

// Applies `raw[key]` onto `target[key]` only when `check` accepts it; otherwise `target` (already a
// valid baseline value) is kept and a warning explains what was dropped. A field simply absent from
// the draft (undefined) is not a warning - an older draft predating a new field is normal, not an
// error.
function overlay<T, K extends keyof T>(target: T, raw: Record<string, unknown> | null, key: K, check: (value: unknown) => boolean, label: string, warnings: string[]): T {
  const value = raw?.[key as string];
  if (value === undefined) return target;
  if (!check(value)) { warnings.push(`${label}: could not be restored`); return target; }
  return { ...target, [key]: value as T[K] };
}

const DATE_SOURCES = new Set(["", "manual", "coterm"]);

const overlaySectionDates = (baseline: EditorSectionDates, raw: unknown, label: string, warnings: string[]): EditorSectionDates => {
  const rawDates = record(raw);
  if (!rawDates) { if (raw !== undefined) warnings.push(`${label} dates: could not be restored`); return baseline; }
  let dates = overlay(baseline, rawDates, "startDate", isStr, `${label} start date`, warnings);
  dates = overlay(dates, rawDates, "endDate", isStr, `${label} end date`, warnings);
  const source = rawDates.endDateSource;
  if (source !== undefined) {
    if (isStr(source) && DATE_SOURCES.has(source)) dates = { ...dates, endDateSource: source as EditorSectionDates["endDateSource"] };
    else warnings.push(`${label} end date source: could not be restored`);
  }
  dates = overlay(dates, rawDates, "cotermTargetIds", isNullableStr, `${label} co-term target`, warnings);
  dates = overlay(dates, rawDates, "cotermTargetLabel", isNullableStr, `${label} co-term label`, warnings);
  return dates;
};

// Every IQuoteLineItem field a staff member actually enters or edits pre-save. Deliberately
// excludes server-only computed output (charge_start_date/charge_end_date/billable_days/
// proration_detail, raw_*_unit_price, applied_discount_pct, adjustment_reason,
// is_commission_eligible, promo_eligibility*, coterm_verified_at) - all of that is re-derived by
// the next reprice regardless of what a draft says, so trusting stale values from a draft would
// only risk showing numbers that don't match what Save will actually charge.
const STRING_FIELDS: (keyof IQuoteLineItem)[] = ["product_id", "sku_id", "sku_title"];
const NULLABLE_STRING_FIELDS: (keyof IQuoteLineItem)[] = [
  "note", "price_unavailable_reason", "promo_id", "promo_end_date",
  "start_date", "end_date", "coterm_target_subscription_id", "coterm_target_label", "renewal_date",
];
const NULLABLE_NUMBER_FIELDS: (keyof IQuoteLineItem)[] = [
  "manual_msrp_unit_price", "manual_cost_unit_price", "requested_discount_pct",
  "microsoft_promo_pct", "customer_promo_pct", "promo_min_quantity", "promo_max_quantity",
];
const BOOL_FIELDS: (keyof IQuoteLineItem)[] = ["is_price_manual", "promo_rejected"];
const SEGMENTS = new Set(["", "Commercial", "Charity", "Education", "Government"]);
const TERMS = new Set(["", "P1M", "P1YM", "P1YA"]);
const PROMO_SOURCES = new Set([null, "partner_center", "manual"]);
const DATE_SOURCE_VALUES = new Set([null, "manual", "coterm"]);

const overlayLineItem = (raw: unknown, label: string, warnings: string[]): IQuoteLineItem | null => {
  const rawItem = record(raw);
  if (!rawItem) { warnings.push(`${label}: could not be restored`); return null; }

  let item: IQuoteLineItem = {
    product_id: "", sku_id: "", sku_title: "", segment: "", term_duration: "", quantity: 1,
    total_price: null, is_price_manual: false, is_price_unavailable: false, price_unavailable_reason: null, note: null,
  };
  STRING_FIELDS.forEach(key => { item = overlay(item, rawItem, key, isStr, `${label} ${key}`, warnings); });
  NULLABLE_STRING_FIELDS.forEach(key => { item = overlay(item, rawItem, key, isNullableStr, `${label} ${key}`, warnings); });
  NULLABLE_NUMBER_FIELDS.forEach(key => { item = overlay(item, rawItem, key, isNullableNum, `${label} ${key}`, warnings); });
  BOOL_FIELDS.forEach(key => { item = overlay(item, rawItem, key, isBool, `${label} ${key}`, warnings); });
  item = overlay(item, rawItem, "quantity", value => isNum(value) && value > 0, `${label} quantity`, warnings);

  if (rawItem.segment !== undefined) {
    if (isStr(rawItem.segment) && SEGMENTS.has(rawItem.segment)) item = { ...item, segment: rawItem.segment as IQuoteLineItem["segment"] };
    else warnings.push(`${label} segment: could not be restored`);
  }
  if (rawItem.term_duration !== undefined) {
    if (isStr(rawItem.term_duration) && TERMS.has(rawItem.term_duration)) item = { ...item, term_duration: rawItem.term_duration as IQuoteLineItem["term_duration"] };
    else warnings.push(`${label} term: could not be restored`);
  }
  if (rawItem.promo_source !== undefined) {
    if (PROMO_SOURCES.has(rawItem.promo_source as string | null)) item = { ...item, promo_source: rawItem.promo_source as IQuoteLineItem["promo_source"] };
    else warnings.push(`${label} promotion: could not be restored`);
  }
  if (rawItem.inherits_section_dates !== undefined) {
    if (isBool(rawItem.inherits_section_dates)) item = { ...item, inherits_section_dates: rawItem.inherits_section_dates };
    else warnings.push(`${label} date inheritance: could not be restored`);
  }
  if (rawItem.end_date_source !== undefined) {
    if (DATE_SOURCE_VALUES.has(rawItem.end_date_source as string | null)) item = { ...item, end_date_source: rawItem.end_date_source as IQuoteLineItem["end_date_source"] };
    else warnings.push(`${label} end date source: could not be restored`);
  }
  return item;
};

const overlaySection = (baseline: EditorSection, raw: unknown, index: number, warnings: string[]): EditorSection => {
  const rawSection = record(raw);
  const label = `Section ${index + 1}`;
  if (!rawSection) { warnings.push(`${label}: could not be restored`); return baseline; }

  let section = overlay(baseline, rawSection, "name", isStr, `${label} name`, warnings);
  section = { ...section, dates: overlaySectionDates(section.dates, rawSection.dates, label, warnings) };

  if (Array.isArray(rawSection.items)) {
    const items = rawSection.items
      .map((rawItem, itemIndex) => overlayLineItem(rawItem, `${label}, line ${itemIndex + 1}`, warnings))
      .filter((item): item is IQuoteLineItem => item !== null);
    section = { ...section, items };
  } else if (rawSection.items !== undefined) {
    warnings.push(`${label} line items: could not be restored`);
  }
  return section;
};

// Never throws, never trusts the draft's shape. Starts from `baseline` - a normal, already-valid
// editor state (a fresh new quote, or the freshly loaded existing quote) - and overlays only what
// `rawDraft` offers in a recognized, valid shape. A stale/incompatible/malformed draft degrades to
// a partial restore (baseline values kept, a warning recorded) rather than a hard failure or a
// silent full discard.
export const applyQuoteDraft = (baseline: DraftableEditorState, rawDraft: unknown): { state: DraftableEditorState; warnings: string[] } => {
  const warnings: string[] = [];
  const raw = record(rawDraft);
  if (!raw) return { state: baseline, warnings: rawDraft == null ? [] : ["This draft could not be read - nothing was restored."] };

  let state = overlay(baseline, raw, "prospectName", isStr, "Prospect name", warnings);
  state = overlay(state, raw, "name", isStr, "Quote name", warnings);
  state = overlay(state, raw, "description", isStr, "Description", warnings);
  state = overlay(state, raw, "internalNote", isStr, "Internal note", warnings);
  state = overlay(state, raw, "hasAffiliate", isBool, "Has Affiliate", warnings);
  state = overlay(state, raw, "affiliateName", isStr, "Affiliate name", warnings);
  state = overlay(state, raw, "customerGuid", isNullableStr, "Customer", warnings);

  const rawDiscounts = record(raw.discounts);
  if (rawDiscounts) {
    let discounts = state.discounts;
    discounts = overlay(discounts, rawDiscounts, "monthly", isPercent, "Monthly discount", warnings);
    discounts = overlay(discounts, rawDiscounts, "installment", isPercent, "Installment discount", warnings);
    discounts = overlay(discounts, rawDiscounts, "annual", isPercent, "Annual discount", warnings);
    state = { ...state, discounts };
  } else if (raw.discounts !== undefined) {
    warnings.push("Discounts: could not be restored");
  }

  if (Array.isArray(raw.sections) && raw.sections.length > 0) {
    const sections = raw.sections.map((rawSection, index) => overlaySection(baseline.sections[index] ?? newEditorSection(), rawSection, index, warnings));
    state = { ...state, sections };
  } else if (raw.sections !== undefined) {
    warnings.push("Line items: could not be restored");
  }

  return { state, warnings };
};
